import { useAsyncModal } from '@ipfabric/frontend-shared-assets/utils/react/useAsyncModal';
import { type FC, type ReactNode, memo } from 'react';
import { Button } from '../Button/Button';
import { Dialog } from '../Dialog/Dialog';
import { useRouteTransitionBlocker } from './useRouteTransitionBlocker';

import classNames from 'classnames';
import { PortalToRoot } from '../PortalToRoot/PortalToRoot';
import { isFeatureFlagEnabled } from '../utils/featureFlags';

interface Props {
  dirty: boolean;
  msg?: {
    title?: string;
    content?: ReactNode;
  };
}

const LeaveDialogStateIndicator = (props: { dirty: boolean }) => (
  <PortalToRoot>
    <div
      className={classNames(
        // Positioned behind DevToolbar button (z-[9999]) so it shows as a status ring around it
        'fixed bottom-3 right-3 size-14 z-[9998] opacity-50 border-[4px] rounded-full text-contrast flex items-center justify-center pointer-events-none',
        props.dirty ? 'border-status-error' : 'border-status-success',
      )}
    />
  </PortalToRoot>
);

/**
 * Trigger a confirmation dialog when the user tries to leave a page with unsaved changes.
 *
 * Unsaved changes are simply indicated by dirty=true prop.
 */
const BareLeaveDialog: FC<Props> = (props) => {
  const confirmLeaveModal = useAsyncModal<boolean>();

  useRouteTransitionBlocker({
    blockTransitions: props.dirty,
    onBlock: () =>
      suppressed ? Promise.resolve(true) : confirmLeaveModal.show(),
  });

  return (
    <>
      {isFeatureFlagEnabled('ENABLE_LEAVE_DIALOG_DEBUG_INDICATOR') && (
        <LeaveDialogStateIndicator dirty={props.dirty} />
      )}
      {confirmLeaveModal.render((returnFromModal) => (
        <Dialog
          closeOnBackdropClick
          header={{ title: props.msg?.title ?? 'Unsaved changes' }}
          onClose={() => returnFromModal(false)}
          footer={
            <div className='flex justify-between gap-xs'>
              <Button
                accent='negative'
                dataTestId='leave-dialog-leave-button'
                // Closing the modal would cause a state update -> reinitialize transition blocker -> stop transition again
                onClick={() => returnFromModal(true)}
                variant='primary'
              >
                Leave
              </Button>
              <Button
                dataTestId='leave-dialog-cancel-button'
                onClick={() => returnFromModal(false)}
                variant='outline'
              >
                Stay
              </Button>
            </div>
          }
        >
          <div className='text-default'>
            {props.msg?.content ?? 'Do you really want to leave?'}
          </div>
        </Dialog>
      ))}
    </>
  );
};

export const LeaveDialog = memo(BareLeaveDialog);

let suppressed = false;

/**
 * Temporarily suppress the leave dialog within the callback.
 *
 * To be used for operations that both clean the dirty state and trigger a navigation,
 * for example a "Save and go back" button.
 *
 * Declared global for simplicity of use, as one leave dialog per page can be used anyway.
 *
 * Callback argument is intentionally synchronous and shouldn't be extended to async,
 * otherwise we open a time window when dirty state won't be controlled.
 *
 * @example
 * ```
 * <LeaveDialog isDirty={form.dirty} />
 *
 * <Button onClick={() => {
 *   await form.submit();
 *   allowLeave(() =>
 *     navigateBack();
 *   );
 * }}>
 *   Save and go back
 * </Button>
 * ```
 */
export const allowLeave = (callback: () => void) => {
  try {
    suppressed = true;
    callback();
  } finally {
    suppressed = false;
  }
};
